import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, RefreshCw, FileText, Search, X, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import reportConfigurationService, {
  ReportConfiguration,
  ReportType,
  ReportTypeLabels
} from '../../../../services/reportConfigurationService';
import { useAuth } from '../../../../contexts/AuthContext';
import { ReportConfigurationModal } from './ReportConfigurationModal';
import { PDFPreviewModal } from './PDFPreviewModal';

/** Collapsible help section explaining how report configuration and printer routing works */
const HelpSection: React.FC = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-100 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 text-blue-800">
          <Info size={18} />
          <span className="font-medium text-sm">Hvordan fungerer rapportkonfigurasjon?</span>
        </div>
        {expanded ? <ChevronDown size={18} className="text-blue-600" /> : <ChevronRight size={18} className="text-blue-600" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
          <div>
            <p className="font-semibold mb-1">Hva er en rapportkonfigurasjon?</p>
            <p>
              Hver rad i denne tabellen definerer en utskriftsrapport — f.eks. en vareetikett, pakkseddel eller sorteringskasse-etikett.
              Konfigurasjonen bestemmer <strong>hvilken mal</strong> som brukes, <strong>papirstørrelse</strong> og en valgfri <strong>standard-printer</strong> (fallback).
            </p>
          </div>

          <div>
            <p className="font-semibold mb-1">Printer-hierarki (4 nivåer)</p>
            <p className="mb-1">Når systemet skal skrive ut, velges printer etter dette hierarkiet (første treff vinner):</p>
            <ol className="list-decimal list-inside space-y-0.5 ml-2">
              <li><strong>PackZone</strong> — printer konfigurert på sone-nivå (sjelden brukt)</li>
              <li><strong>ProfileReports</strong> — per-profil overstyring for en bestemt rapport-ID</li>
              <li><strong>Profil</strong> — operatørens standard-printer basert på rapporttype (se tabell nedenfor)</li>
              <li><strong>Rapport</strong> — printer satt her i rapportkonfigurasjonen (global fallback)</li>
            </ol>
          </div>

          <div>
            <p className="font-semibold mb-1">Rapporttype → Printer-felt på profil</p>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse w-full">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="border border-blue-200 px-2 py-1 text-left">Rapporttype</th>
                    <th className="border border-blue-200 px-2 py-1 text-left">Profil-felt</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-blue-200 px-2 py-1">1-5: Vare/Lokasjon</td><td className="border border-blue-200 px-2 py-1">Skriver, vareetikett</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">10-19: Pakkseddel</td><td className="border border-blue-200 px-2 py-1">Skriver, pakkseddel</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">20-29: Fraktetikett</td><td className="border border-blue-200 px-2 py-1">Skriver, fraktetikett</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">30-39: Fraktbrev</td><td className="border border-blue-200 px-2 py-1">Skriver, fraktbrev</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">40-49: Kollietikett</td><td className="border border-blue-200 px-2 py-1">Skriver, pakke/kolli</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">50-59: SSCC/Pall</td><td className="border border-blue-200 px-2 py-1">Skriver, SSCC etikett</td></tr>
                  <tr><td className="border border-blue-200 px-2 py-1">70-79: Sorteringskasse</td><td className="border border-blue-200 px-2 py-1">Skriver, sorteringskasse</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="font-semibold mb-1">Når trenger du en rad her?</p>
            <p>
              Du trenger <strong>ikke</strong> en rad for at utskrift skal fungere — systemet bruker profil-innstillinger og hardkodede standardverdier for papirstørrelse.
              Opprett en rad her hvis du vil <strong>overstyre papirstørrelse</strong> (bredde/lengde/liggende),
              sette en <strong>global fallback-printer</strong>, eller aktivere <strong>skylagring</strong> av etiketter.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

interface ReportConfigurationListProps {
  onBack?: () => void;
}

export const ReportConfigurationList: React.FC<ReportConfigurationListProps> = ({ onBack }) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo;
  const [reports, setReports] = useState<ReportConfiguration[]>([]);
  const [filteredReports, setFilteredReports] = useState<ReportConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<number | 'all'>('all');

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportConfiguration | null>(null);
  const [previewReportId, setPreviewReportId] = useState<number | null>(null);

  useEffect(() => {
    loadReports();
  }, [companyNo]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, filterType]);

  const loadReports = async () => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);
    try {
      const data = await reportConfigurationService.getAll(companyNo);
      setReports(data);
    } catch (err: any) {
      console.error('Failed to load reports:', err);
      setError(err.message || 'Kunne ikke laste rapporter');
    } finally {
      setLoading(false);
    }
  };

  const filterReports = () => {
    let filtered = [...reports];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.reportType === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.description.toLowerCase().includes(term) ||
        r.reportName.toLowerCase().includes(term) ||
        (r.printerName && r.printerName.toLowerCase().includes(term))
      );
    }

    setFilteredReports(filtered);
  };

  const handleCreate = () => {
    setSelectedReport(null);
    setShowEditModal(true);
  };

  const handleEdit = (report: ReportConfiguration) => {
    setSelectedReport(report);
    setShowEditModal(true);
  };

  const handleDelete = async (report: ReportConfiguration) => {
    if (!companyNo) return;

    const confirmed = window.confirm(
      `Er du sikker på at du vil slette rapporten "${report.description}"?`
    );

    if (!confirmed) return;

    try {
      await reportConfigurationService.delete(companyNo, report.id);
      await loadReports();
    } catch (err: any) {
      console.error('Failed to delete report:', err);
      alert(`Kunne ikke slette rapport: ${err.message}`);
    }
  };

  const handlePreview = (reportId: number) => {
    setPreviewReportId(reportId);
    setShowPreviewModal(true);
  };

  const handleModalClose = async (refresh: boolean) => {
    setShowEditModal(false);
    setSelectedReport(null);
    if (refresh) {
      await loadReports();
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-2">Laster rapporter...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Feil: {error}</p>
        <button
          onClick={loadReports}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Rapportkonfigurasjon</h2>
            <p className="text-sm text-gray-600 mt-1">
              Administrer utskriftsrapporter og etiketter
            </p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Ny Rapport
        </button>
      </div>

      {/* Help / Info Section */}
      <HelpSection />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Søk i beskrivelse, navn eller printer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Alle typer</option>
              {Object.entries(ReportTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Nullstill filter
            </button>
            <button
              onClick={loadReports}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Oppdater"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-600">
          Viser {filteredReports.length} av {reports.length} rapporter
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beskrivelse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rapportnavn
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Printer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensjoner
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <FileText className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>Ingen rapporter funnet</p>
                    {(searchTerm || filterType !== 'all') && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-blue-600 hover:text-blue-700"
                      >
                        Nullstill filter
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {report.reportID}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {ReportTypeLabels[report.reportType as ReportType] || report.reportType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                      {report.reportName}
                      {report.reportName.startsWith('STANDARD:') && (
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                          Standard
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {report.printerName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {report.externalPrinterWidth && report.externalPrinterLength ? (
                        <>
                          {report.externalPrinterWidth} × {report.externalPrinterLength} mm
                          {report.externalPrinterLandscape && (
                            <span className="ml-1 text-xs text-gray-500">(L)</span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {report.reportName.startsWith('STANDARD:') && (
                          <button
                            onClick={() => handlePreview(report.id)}
                            className="p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded"
                            title="Forhåndsvisning"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(report)}
                          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded"
                          title="Rediger"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(report)}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                          title="Slett"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <ReportConfigurationModal
          isOpen={showEditModal}
          onClose={handleModalClose}
          report={selectedReport}
        />
      )}

      {showPreviewModal && previewReportId && (
        <PDFPreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewReportId(null);
          }}
          reportId={previewReportId}
        />
      )}
    </div>
  );
};
